import { ObjectTypes } from "@common/enums/object-types";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { CollectionRecord } from '@common/data-models/records/collection-record';
import { Record } from '@common/data-models/records/record';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { AttachmentList } from '@common/data-models/complex-types/attachment-list';
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { AssignmentRecord } from "@playbook/data-models/records/assignment-record";
import { FieldDef } from "@common/data-models/service/field-def";

import { IPlaybookService } from "@playbook/interfaces/i-playbook-service";
import { Address } from "@suite/common/data-models/complex-types/address";
import { IAppService } from "@suite/i-app.service";

export class TopicRecord extends CollectionRecord {
    public static playbookService: IPlaybookService;

    protected getExcludedFields(): { [id: string]: boolean } {
        let excluded: { [id: string]: boolean } = {};
        excluded[PSTFieldNames.PSTTopicSlug] = true;
        excluded[PSTFieldNames.PSTTopicSummary] = true;
        excluded[PSTFieldNames.PSTTopicDepartment] = true;
        excluded[PSTFieldNames.PSTTopicTag] = true;
        excluded[PSTFieldNames.PSTTopicStatus] = true;
        excluded[PSTFieldNames.PSTTopicType] = true;
        excluded[PSTFieldNames.PSTTopicStoryOwner] = true;
        excluded[PSTFieldNames.Owner] = true;
        excluded[PSTFieldNames.PSTExternalFiles] = true;
        excluded[PSTFieldNames.PSTApproved] = true;
        return excluded;
    }

    public initialize() {
        this.type = ObjectTypes.TOPIC;
        super.initialize();

        this.categories = new MultiValue<string>();
        this.tags = new LookupList();
        this.department = new MultiValue<string>()
        this.topicType = new MultiValue<string>()
        this.attachments = new AttachmentList();
        this.storyOwner = new MultiValue<string>();

        super.setupRequiredFieldsGroups("Topic");
        // Add a group for assignments
        this.requiredFieldsGroups["Assignments"] = true;
    }

    public get categories(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicCategory); }
    public set categories(value: MultiValue<string>) { this.set(PSTFieldNames.PSTTopicCategory, value); }

    public get department(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment); }
    public set department(value: MultiValue<string>) { this.set(PSTFieldNames.PSTTopicDepartment, value); }

    public get notes(): string { return this.getAs<string>(PSTFieldNames.PSTTopicNotes); }
    public set notes(value: string) { this.set(PSTFieldNames.PSTTopicNotes, value); }

    public get topicSlug(): string { return this.getAs<string>(PSTFieldNames.PSTTopicSlug); }
    public set topicSlug(value: string) { this.set(PSTFieldNames.PSTTopicSlug, value); }

    public get status(): string { return this.getAs<string>(PSTFieldNames.PSTTopicStatus); }
    public set status(value: string) { this.set(PSTFieldNames.PSTTopicStatus, value); }

    public get source(): string { return this.getAs<string>(PSTFieldNames.PSTTopicSource); }
    public set source(value: string) { this.set(PSTFieldNames.PSTTopicSource, value); }

    public get storyOwner(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicStoryOwner); }
    public set storyOwner(value: MultiValue<string>) { this.set(PSTFieldNames.PSTTopicStoryOwner, value); }

    public get summary(): string { return this.getAs<string>(PSTFieldNames.PSTTopicSummary); }
    public set summary(value: string) { this.set(PSTFieldNames.PSTTopicSummary, value); }

    public get tags(): LookupList { return this.getAs<LookupList>(PSTFieldNames.PSTTopicTag, new LookupList()); }
    public set tags(value: LookupList) { this.set(PSTFieldNames.PSTTopicTag, value); }

    public get topicEventStart(): DateTime { return this.getAs<DateTime>(PSTFieldNames.PSTTopicEventStartDate); }
    public set topicEventStart(value: DateTime) { this.set(PSTFieldNames.PSTTopicEventStartDate, value); }

    public get topicType(): MultiValue<string> { return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicType, new MultiValue<string>()); }
    public set topicType(value: MultiValue<string>) { this.set(PSTFieldNames.PSTTopicType, value); }

    public get fileDate(): DateTime { return this.getAs<DateTime>(PSTFieldNames.PSTTopicFileDate); }
    public set fileDate(value: DateTime) { this.set(PSTFieldNames.PSTTopicFileDate, value); }

    public get attachments(): AttachmentList { return this.getAs<AttachmentList>(PSTFieldNames.PSTExternalFiles); }
    public set attachments(value: AttachmentList) { this.set(PSTFieldNames.PSTExternalFiles, value); }

    public get pstApproved(): boolean { return this.getAs<boolean>(PSTFieldNames.PSTApproved); }
    public set pstApproved(value: boolean) { this.set(PSTFieldNames.PSTApproved, value); }

    public get location(): Address { return this.getAs<Address>(PSTFieldNames.PSTTopicAddress, new Address()); }
    public set location(value: Address) { this.set(PSTFieldNames.PSTTopicAddress, value); }

    get children(): AssignmentRecord[] { return this._children as AssignmentRecord[]; }
    set children(value: AssignmentRecord[]) { this._children = value; }

    public copyDataAndChildrenTo(target: CollectionRecord, preserveGuids?: boolean, isTemplate?:boolean, fieldsToSkip?: { [field: string]: boolean }) {
        this.copyDataTo(target, preserveGuids, fieldsToSkip);
        if (isTemplate) {
            fieldsToSkip[PSTFieldNames.PSTTopicSlug.toLowerCase()] = false;
        }
        for (let child of this.getChildren()) {
            let newChild = new AssignmentRecord();
            newChild.guid = undefined;
            child.copyDataTo(newChild, preserveGuids, fieldsToSkip);
            target.addChildren(newChild);
        }
    }
    public getSecurityPrivsWrite(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false;
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].topic.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
        }
    }

    public getSecurityPrivsWriteChildren(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false;
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
        }
    }

    public getSecurityPrivsRead(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false;
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].topic.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
        }
    }

    public getSecurityPrivsReadChildren(appService: IAppService): boolean {
        let desk = this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicDepartment);
        let ret = false;
        if (desk.length > 0) {
            for (let val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
                }
            }
            return ret;
        } else {
            return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
        }
    }

    get getRequiredProperties(): { [id: string]: FieldDef } {
        return TopicRecord.playbookService.TopicRequiredFieldDefsDict;
    }

    public checkRequiredProperties() {
        super.checkRequiredProperties();
        // Check assignments
        this.requiredFieldsGroups['Assignments'] = true;
        let children = this.getChildren();
        if (children.length > 0) {
            for (let child of children) {
                child.checkRequiredProperties();
                if (!child.isRequiredFieldsPopulated) {
                    this.isRequiredFieldsPopulated = false;
                    this.requiredFieldsGroups['Assignments'] = false;
                    return;
                }
            }
        }
    }

    private static _fieldDefsDict: { [id: string]: FieldDef } = null;
    public get fieldDefsDict(): { [id: string]: FieldDef } {
        if (!TopicRecord._fieldDefsDict) {
            TopicRecord._fieldDefsDict = {};
            for (let key in Record.globalTablesService.FieldDefsDict) {
                if (Record.globalTablesService.FieldDefsDict[key].TopicProp) {
                    TopicRecord._fieldDefsDict[key] = Record.globalTablesService.FieldDefsDict[key];
                }
            }
        }
        return TopicRecord._fieldDefsDict;
    }
}

